import { fetchWithCsrf } from '../api/fetch';

/**
 * The one-tap repurchase a paywall offers a member who has paid before
 * (`/psp/api/one-tap`, PHP `OneTapOffer`): their last package with their last
 * payment method, at the current price. A bonus coupon never changes the
 * price: `bonusCredits` are the extra credits the member's offered coupon adds
 * to the package (0 without one).
 */
export interface OneTapOffer {
  optionId: number;
  providerName: string;
  methodId: string;
  methodSecondaryId: string | null;
  methodName: string;
  credits: number;
  bonusCredits: number;
  currencyIso: string;
  amount: number;
  amountInCents: number;
  prettyPrice: string;
}

/**
 * The member's offer, or null (first-time buyer, package or method no longer
 * offered, or the request failed: the paywall then links to the payment page).
 */
export const fetchOneTapOffer = async (signal?: AbortSignal): Promise<OneTapOffer | null> => {
  try {
    const response = await fetch('/psp/api/one-tap', signal ? { signal } : {});
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data?.offer ?? null;
  } catch {
    return null;
  }
};

/**
 * The messages of a refused start (`{errors: {attribute: [message]}}`). The
 * same shape as Payment.tsx's startPaymentErrors(), kept here so the paywalls
 * do not pull the lazily loaded payment page into their chunk.
 */
const refusalMessages = (errors: unknown): string[] => {
  if (!errors || typeof errors !== 'object') {
    return [];
  }
  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((message): message is string => typeof message === 'string' && message !== '');
  return Array.from(new Set(messages));
};

export type OneTapStartResult =
  | { redirectUrl: string }
  | { errors: string[] };

/**
 * Starts the offer through `/psp/default/start` (`oneTap=1`). The server
 * refuses anything but the member's current offer; `returnRoute` is where
 * `PaymentReturn` brings the member back to after paying.
 */
export const startOneTap = async (offer: OneTapOffer, returnRoute: string): Promise<OneTapStartResult> => {
  const payload = new URLSearchParams();
  payload.append('oneTap', '1');
  payload.append('returnUrl', returnRoute);
  payload.append('StartPayment[providerName]', offer.providerName);
  payload.append('StartPayment[methodId]', offer.methodId);
  payload.append('StartPayment[optionId]', String(offer.optionId));
  try {
    const response = await fetchWithCsrf('/psp/default/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString(),
    });
    const res = await response.json();
    if (res?.data?.redirectUrl) {
      return { redirectUrl: res.data.redirectUrl };
    }
    return { errors: res?.unexpected ? [] : refusalMessages(res?.errors) };
  } catch {
    return { errors: [] };
  }
};
